<template>
  <div>
    <nav :class="[$style.filters, 'nav-bar', 'space-12-16-above']">
      <ul>
        <li
          v-for="(item, idx) in artworkFilterSubtypes"
          :key="idx"
          :class="[{ active: checkIsSelected(item.name) }, 'nav-subtypes']"
        >
          <button
            :id="`${item.name}-button`"
            :for="item.name"
            :class="['button-filter', checkIsSelected(item.name) ? 'up' : 'down']"
            :aria-controls="item.name"
            :aria-expanded="checkIsSelected(item.name)"
            aria-haspopup="true"
            type="button"
            @click.stop.prevent="toggle(item.name)"
            v-html="`${item.label}<span class='screenreader-only'> Filter</span>`"
          ></button>
          <ul
            v-if="adjustedFilters && Object.keys(adjustedFilters).length"
            :id="item.name"
            :ref="
              (el) => {
                dropdownRefs[`${item.name}`] = el;
              }
            "
            :aria-labelledby="`${item.name}-button`"
            class="nav-items"
            role="menu"
          >
            <li
              v-for="(menu, index) in adjustedFilters[item.name].nodes"
              :key="index"
              role="none"
              :class="{ pressed: activeFilter === menu.slug }"
            >
              <ArtworkNavListItem
                :menu="menu"
                :path="route.path"
                class="button"
                :ga4-event="
                  JSON.stringify({
                    filter_location: 'collection',
                    filter_type: 'artwork',
                    filter_value: `${menu.name}`,
                    gtm_tag: 'filter',
                  })
                "
                role="menuitem"
              ></ArtworkNavListItem>
            </li>
          </ul>
        </li>
      </ul>
    </nav>
    <nav :class="$style.backLink">
      <BackLink
        v-if="back"
        component="ArtworkTermArtist"
        group="artwork"
        label="Collection Online"
        name="CollectionOnline"
      ></BackLink>
    </nav>
  </div>
</template>

<script setup lang="ts">
import ArtworkNavListItem from '@/components/artwork/ArtworkNavListItem.vue';
import BackLink from '@/components/common/BackLink.vue';
import { Filters } from '@/types/artwork.interface.ts';
import { onMounted, ref, watch } from 'vue';
import { useAnalytics, useFilterInteractions } from '@/composables/Common.js';
import { useRoute } from 'vue-router';

const route = useRoute();

const props =
  withDefaults(
    defineProps<{
    activeFilter?: string;
    back?: boolean;
    filters: {
      artworkTypes: {
        nodes: Array<Filters>;
      };
      decades: {
        nodes: Array<Filters>;
      };
      movements: {
        nodes: Array<Filters>;
      };
      sites: {
        nodes: Array<Filters>;
      };
      specialCollections: {
        nodes: Array<Filters>;
      }
    }
  }>(),
  { back: true },
);

const { ga4Event } = useAnalytics();

const adjustedFilters = ref({});

const alphabet = [
  'A',
  'B',
  'C',
  'D',
  'E',
  'F',
  'G',
  'H',
  'I',
  'J',
  'K',
  'L',
  'M',
  'N',
  'O',
  'P',
  'Q',
  'R',
  'S',
  'T',
  'U',
  'V',
  'W',
  'X',
  'Y',
  'Z',
];

// this ordering describes the nav order. "Artists" is always first
const artworkFilterSubtypes = [
  { name: 'artists', label: 'Artists' },
  { name: 'decades', label: 'Dates' },
  { name: 'artworkTypes', label: 'Mediums' },
  { name: 'movements', label: 'Movements' },
  { name: 'specialCollections', label: 'Special Collections' },
  { name: 'sites', label: 'Venues' },
];
const dropdownRefs = ref<Array<HTMLElement>>([]);
const selected = ref('');
const siteSelected = [
  'solomon-r-guggenheim-museum',
  'peggy-guggenheim-collection',
  'guggenheim-museum-bilbao',
];

const { selectNav } = useFilterInteractions(selected);

watch(
  () => props.filters,
  (filter) => {
    if (filter && filter.sites.nodes.length !== 3) {
      prepareSites();
    }
  },
);

onMounted(() => {
  if (Object.keys(props.filters).length) {
    prepareSites();
  }
});

function checkIsSelected(selection: string): boolean {
  return selection === selected.value;
}

function prepareSites(): void {
  // only show selected venues
  const adjustedSites = [...props.filters.sites.nodes]
    .reverse()
    .filter((site) => siteSelected.includes(site.slug));

  adjustedFilters.value = {
    ...props.filters,
    artists: { nodes: [] },
    sites: { nodes: adjustedSites },
  };

  alphabet.forEach((letter) =>
    adjustedFilters.value.artists.nodes.push({
      __typename: 'artists',
      description: '',
      name: letter,
      slug: letter,
    }),
  );
}

function toggle(selection: string): void {
  selectNav(selection, Object.values(dropdownRefs.value));
  if (selection !== 'artists') {
    ga4Event({
      filter_location: 'collection',
      filter_type: 'artwork',
      filter_value: artworkFilterSubtypes.filter((subtype) => subtype.name === selection)[0].label,
      gtm_tag: 'filter',
    });
  }
}
</script>

<style lang="scss">
@use '@/styles/a11y.module.scss' as *;
@use '@/styles/navigation.module.scss' as *;
</style>

<style lang="scss" module>
.artists {
  a {
    padding: 4px 8px;
    text-decoration: none;

    &:hover {
      text-decoration: underline;

      &::after {
        background: none;
      }
    }

    &:focus {
      color: $white;
    }

    &::after {
      background: none;
    }
  }
}
.back-link {
  margin-top: 0.5rem;
}
.filter {
  @include button-filter;

  & {
    cursor: default;
    display: inline-block;
  }

  &::after {
    content: none;
  }
}

@media (min-width: $break-point) {
  .artists {
    display: inline-block;
    margin-right: 1rem;
  }
  .back-link {
    margin-top: 0;
  }
  .filters {
    > ul {
      padding-bottom: 0.625rem;
      max-width: $desktop-width;

      ul {
        text-align: left;
      }
    }
  }

  .ios {
    display: none;
  }
}
</style>
